"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "Item code",
    rule: "Auto-generated with an I- prefix unless you change prefix, next number, or auto-generate under Configure Item Number Preferences on Add Item. You cannot type a one-off code on the form itself — change the company numbering setting instead",
  },
  {
    topic: "Goods vs Service",
    rule: "A Service item can never track inventory or hold an Inventory Account — the form blocks both",
  },
  {
    topic: "Track Inventory",
    rule: "Cannot be turned off while the item still has stock on hand",
  },
  {
    topic: "Opening stock",
    rule: "Editable per warehouse only until that warehouse's opening batch has been sold or consumed from; editing it re-posts the whole opening journal, not an adjustment",
  },
  {
    topic: "Min / Max Stock Level",
    rule: "When stock crosses min or max, a real-time notification is sent to users who have alerts on under Settings → Notifications. Also drives the Stock Status filter on View Inventory",
  },
  {
    topic: "Item Type",
    rule: "Mostly a label. The only places it changes behaviour are the FG dropdown on Purchase Orders and the raw-material/scrap pickers on Submit Production",
  },
  {
    topic: "Producing this item",
    rule: "Sales Order → Production checks the item's Process Route or BOM setup first and blocks if it's missing. Creating a Production Order directly does not check it",
  },
  {
    topic: "Sellable / Purchasable",
    rule: "Purchasable correctly hides the item from every purchase document's picker. Sellable currently does not hide it from Invoice, Quote, Sales Order, Delivery Challan, or Recurring Invoice pickers — only Credit Note respects it",
  },
  {
    topic: "Accounts lock after use",
    rule: "Sales Account locks once the item is on an Invoice or Credit Note; Purchase Account once it's on a Bill or Debit Note; Inventory Account once the item has any stock",
  },
  {
    topic: "Inventory Sales Method",
    rule: "FIFO is the only method actually implemented. Nearest Expiry Date, Sales to Option, and LIFO are visible in the dropdown but marked Coming soon and don't change anything if picked",
  },
  {
    topic: "Deactivating",
    rule: "Blocked while the item sits on any open document across 15 document types (quotes, invoices, orders, production, and more). Reactivating has no such check",
  },
  {
    topic: "Deleting",
    rule: "A real, permanent delete. Blocked if the item is referenced anywhere at all, including inside another item's BOM or scrap list",
  },
  {
    topic: "Quality standards, BOE, UOM Convert",
    rule: "IQC/FQC/OQC, BOE Mandatory, and UOM Convert all save correctly but don't yet gate or affect any transaction",
  },
  {
    topic: "Preferred Vendor, Print On Document",
    rule: "Both save correctly but are informational only — neither affects a Purchase Order or a printed document",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Item Type, Category, and what actually tracks stock" },
      { heading: "2. Setting opening stock per warehouse" },
      { heading: "3. Minimum and Maximum Stock Level trigger real alerts" },
      { heading: "4. What Sellable and Purchasable actually control" },
      { heading: "5. Producing this item: Process Route vs BOM-based" },
      { heading: "6. Where this item posts in your books" },
      { heading: "7. How stock cost is calculated" },
      { heading: "8. Fields that are stored but don't change behaviour yet" },
      { heading: "9. Deactivating and deleting" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function ItemMasterPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Item Master
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone maintaining the products, materials, and services the
            business buys, sells, or manufactures
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            This is the{" "}
            <span className="geist-medium text-[#111827]">central catalogue</span>{" "}
            every transaction in ABSS Books draws from. Invoices, purchase
            orders, bills, and production orders all reference an item from
            here. Every item is built from{" "}
            <span className="geist-medium text-[#111827]">six tabs</span> that
            cover how it&apos;s purchased, sold, stocked, and manufactured, and
            the choices you make on those tabs genuinely change how the item
            behaves elsewhere in the system.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Item Type, Category, stock tracking */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Item Type, Category, and what actually tracks stock
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Two fields look similar but do very different jobs.{" "}
            <span className="geist-medium text-[#111827]">Item Category</span>{" "}
            is the real switch;{" "}
            <span className="geist-medium text-[#111827]">Item Type</span> is
            mostly a label.
          </p>
          <Definitions
            items={[
              {
                term: "Item Code",
                desc: "Auto-generated (default I- prefix). Change prefix, next number, or auto-generate under Configure Item Number Preferences on Add Item — you cannot type a one-off code on the form.",
              },
              {
                term: "Item Category (Goods or Service)",
                desc: "A Service item never gets an inventory record and never moves stock on any document, though it can still be added to invoices, bills, and every other transaction.",
              },
              {
                term: "Track Inventory",
                desc: "On a Goods item, this is what actually turns stock movement on or off when the item appears on a transaction. Turning it off doesn't delete the item's inventory record, and it can't be turned off while stock is still on hand.",
              },
              {
                term: "Item Type",
                desc: "RM, FG, SEMI RM, TRADE, CONSUMABLE, or SCRAP is mostly a reporting label. It only changes real behaviour in two places: the FG dropdown when adding a Purchase Order, and the raw-material and scrap pickers on Submit Production.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Item list showing Type and Status columns across Goods and Service items." />

          {/* 2. Opening stock */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Setting opening stock per warehouse
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The{" "}
            <span className="geist-medium text-[#111827]">Inventory Data</span>{" "}
            tab lists every company warehouse with its own opening-stock row,
            and what you enter there posts straight to the ledger.
          </p>
          <Definitions
            items={[
              {
                term: "Total Opening Stock",
                desc: "Adds up automatically from whatever you enter per warehouse below it.",
              },
              {
                term: "The Active checkbox per warehouse",
                desc: "Doesn't currently gate anything. You can leave a warehouse unchecked and still enter opening stock there, and it still posts.",
              },
              {
                term: "Editing opening stock later",
                desc: "Only works until that warehouse's opening batch has actually been sold or consumed from. After that, the opening quantity is locked.",
              },
              {
                term: "What an edit does",
                desc: "Recalculates the total and reposts the whole opening-balance journal entry from scratch, rather than adding an adjustment on top.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the per-warehouse opening stock table on the Inventory Data tab." />

          {/* 3. Min/Max */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Minimum and Maximum Stock Level trigger real alerts
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Unlike several fields on this form, these two are genuinely wired
            up.
          </p>
          <Definitions
            items={[
              {
                term: "Minimum / Maximum Stock Level",
                desc: "Checked every time this item's stock changes through an Invoice, Delivery Challan, or Recurring Invoice, and again when the item itself is created or edited.",
              },
              {
                term: "Crossing either boundary",
                desc: "Sends a real-time notification. Users only receive it if Settings → Notifications has their alerts on.",
              },
              {
                term: "View Inventory",
                desc: "Shows a Stock Status for each item: Below Minimum, Above Maximum, or Normal. You can filter the report by that status.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Stock Status column and filter tabs on View Inventory." />

          {/* 4. Sellable / Purchasable */}
          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. What Sellable and Purchasable actually control
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Both checkboxes are meant to hide the item from pickers it
            shouldn&apos;t appear in. Right now, only one of them fully does
            that.
          </p>
          <Definitions
            items={[
              {
                term: "Purchasable",
                desc: "Works as expected. Turn it off and the item disappears from every purchase document's item picker: Purchase Order, GRPO, Bill, Debit Note, Recurring Bill.",
              },
              {
                term: "Sellable",
                desc: "Currently only respected on Credit Note. Turning it off does not remove the item from Invoice, Quote, Sales Order, Delivery Challan, or Recurring Invoice pickers.",
              },
            ]}
          />
          <Callout type="note">
            Neither flag is enforced on the server once a document is being
            saved. They only control what shows up in the dropdown.
          </Callout>
          <Screenshot caption="Screenshot: the Sellable and Purchasable checkboxes on the Add Item form." />

          {/* 5. Production */}
          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Producing this item: Process Route vs BOM-based
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <span className="geist-medium text-[#111827]">Production Manage Method</span>{" "}
            decides how this item gets made, and it&apos;s checked differently
            depending on how the production order starts.
          </p>
          <Definitions
            items={[
              {
                term: "BOM-based",
                desc: "The item is produced from a bill of materials you build on the item itself.",
              },
              {
                term: "Process Route",
                desc: "The item is produced by routing it through a Recipe of processes instead.",
              },
              {
                term: "From a Sales Order",
                desc: "Sending a sold item to production checks its Process Route or BOM setup first and blocks with an error if it isn't configured.",
              },
              {
                term: "Directly on Add Production",
                desc: "This check doesn't happen. You can start a production order without the item's production data being complete.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Production Data tab with Process Route and BOM-based options." />

          {/* 6. Accounts */}
          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Where this item posts in your books
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Three accounts on the item decide where its money lands in the
            ledger. All three are read live at the moment a document is saved.
          </p>
          <Definitions
            items={[
              {
                term: "Sales Account",
                desc: "Credited when the item is sold on an Invoice or reversed on a Credit Note.",
              },
              {
                term: "Purchase Account",
                desc: "Debited on a Bill or Debit Note, but only for the portion of the item that isn't tracked as stock.",
              },
              {
                term: "Inventory Account",
                desc: "Debited or credited on Invoices, GRPOs, Bills, Credit Notes, Debit Notes, Production, and Inventory Adjustments, anywhere this item's stock value moves.",
              },
            ]}
          />
          <Callout type="warning">
            There&apos;s no fallback account. If the relevant account is
            missing, saving the document fails outright. Each account also
            locks the moment the item is actually used on that kind of
            document.
          </Callout>
          <Screenshot caption="Screenshot: the Sales Account, Purchase Account, and Inventory Account fields on the item form." />

          {/* 7. FIFO */}
          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. How stock cost is calculated
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <span className="geist-medium text-[#111827]">Inventory Sales Method</span>{" "}
            offers four choices, but only one of them does anything.
          </p>
          <Definitions
            items={[
              {
                term: "FIFO",
                desc: "The only method that's actually implemented. Stock is consumed oldest batch first, and that batch's purchase price is what becomes the cost of goods sold.",
              },
              {
                term: "Nearest Expiry Date, Sales to Option, LIFO",
                desc: "All three appear in the dropdown marked Coming soon. Selecting one doesn't change how stock is actually consumed.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Inventory Sales Method dropdown with the three Coming soon options visible." />

          {/* 8. Stored but not enforced */}
          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Fields that are stored but don&apos;t change behaviour yet
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The <span className="geist-medium text-[#111827]">Purchasing Data</span>{" "}
            and <span className="geist-medium text-[#111827]">Sales Data</span>{" "}
            tabs hold a number of fields that save correctly and display back
            on the item, but don&apos;t yet do anything on a transaction. Worth
            knowing before you rely on them.
          </p>
          <Definitions
            items={[
              {
                term: "IQC / FQC / OQC quality standards",
                desc: "Link to a standard defined in Quality Master and display on the item, but nothing checks or blocks a GRPO, Bill, Delivery Challan, or Production Order against them yet.",
              },
              {
                term: "Batch Number and Batch Expiry Mandatory",
                desc: "Enforced in the GRPO and Bill forms themselves, so the app won't let you skip them there, but not enforced at the API level underneath.",
              },
              {
                term: "Import Item Mandatory BOE Number",
                desc: "Saves, but no purchase document actually requires a BOE number yet.",
              },
              {
                term: "Purchase-to-Sales UOM Convert",
                desc: "Stores your purchasing unit and packaging details, but doesn't convert quantities anywhere. Buying in cartons doesn't automatically become pieces in stock.",
              },
              {
                term: "Preferred Vendor",
                desc: "Informational only. It doesn't default the vendor on a new Purchase Order.",
              },
              {
                term: "Print On Document",
                desc: "Saves, but no invoice, PO, or other printed document currently reads it.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the IQC/FQC standard fields and UOM conversion fields on the Purchasing Data tab." />

          {/* 9. Deactivate/Delete */}
          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Deactivating and deleting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Both actions are guarded, but not in the same way or to the same
            degree.
          </p>
          <Definitions
            items={[
              {
                term: "Mark Item As Inactive",
                desc: "Blocked while the item appears on any still-open document, checked across quotes, invoices, orders, production, and more. Closed or cancelled history doesn't block it. Reactivating has no such check.",
              },
              {
                term: "Delete",
                desc: "A real, permanent delete, not a soft flag. Blocked if the item is referenced anywhere at all, including inside another item's own bill of materials or scrap list.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Actions menu on an item's detail page, showing Mark Item As, Edit, and Delete." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[16px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row sm:flex-wrap gap-4"
        >
          <Link
            href="/documentation/books/all-master/item-group"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Item Group <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Categorises items for reporting and price lists
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/warehouse-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Warehouse Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where each item's stock is actually held
            </p>
          </Link>
          <Link
            href="/documentation/books/production/add-production"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Add Production <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where an item's BOM or Recipe method actually gets used
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/inventory-config"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Inventory Config <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              When sales and purchase documents actually move this item's stock
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
